import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  LayoutGrid,
  Plus,
  Trash2,
} from 'lucide-react';

import {
  useMenuMidSectionMembersQuery,
  useMenuMidSectionsQuery,
} from '@/client/api/menus/menus.query';
import {
  useAddMenuSectionMemberMutation,
  useRemoveMenuSectionMemberMutation,
  useReorderMenuSectionMutation,
} from '@/client/api/menus/menus.mutation';
import {
  MENU_SECTION_ADVANCED,
  MENU_SECTION_LABELS,
  MENU_SECTION_PRIMARY_ORDER,
  MENU_SECTION_SITE_HINTS,
  MENU_SECTIONS_WITH_DEDICATED_ORDER,
  MenuSectionMember,
} from '@/client/types/menus';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const memberLabel = (m: MenuSectionMember) =>
  m.name_menu || m.name_friendly || m.name || `Entity #${m.etid}`;

const memberOrder = (m: MenuSectionMember, section: string) =>
  section === 'main'
    ? (m.menu_mid_main_order ?? m.order_by ?? 0)
    : (m.order_by ?? 0);

export const MidSectionsPanel = () => {
  const { data: sections = [], isLoading: sectionsLoading } = useMenuMidSectionsQuery();
  const [selected, setSelected] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [etidInput, setEtidInput] = useState('');
  const [secMenid, setSecMenid] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const orderedSections = useMemo(() => {
    const byId = new Map(sections.map((s) => [s.section, s]));
    const primary = MENU_SECTION_PRIMARY_ORDER.map((id) => byId.get(id)).filter(
      Boolean,
    ) as typeof sections;
    const primaryIds = new Set(primary.map((s) => s.section));
    const rest = sections.filter(
      (s) => !primaryIds.has(s.section) && !MENU_SECTION_ADVANCED.has(s.section),
    );
    const advanced = sections.filter((s) => MENU_SECTION_ADVANCED.has(s.section));
    return { primary: [...primary, ...rest], advanced };
  }, [sections]);

  const effectiveSection =
    selected ??
    orderedSections.primary.find((s) => s.section === 'main')?.section ??
    orderedSections.primary[0]?.section ??
    sections[0]?.section ??
    null;

  const { data: membersResult, isLoading: membersLoading } = useMenuMidSectionMembersQuery(
    effectiveSection,
  );
  const members = membersResult?.members ?? [];

  const addMutation = useAddMenuSectionMemberMutation();
  const removeMutation = useRemoveMenuSectionMemberMutation();
  const reorderMutation = useReorderMenuSectionMutation();

  const hasDedicatedOrder = effectiveSection
    ? MENU_SECTIONS_WITH_DEDICATED_ORDER.has(effectiveSection)
    : false;

  const sortedMembers = useMemo(
    () =>
      [...members].sort(
        (a, b) =>
          memberOrder(a, effectiveSection ?? '') - memberOrder(b, effectiveSection ?? ''),
      ),
    [members, effectiveSection],
  );

  const handleAdd = async () => {
    if (!effectiveSection) return;
    const etid = Number(etidInput);
    if (!Number.isFinite(etid) || etid <= 0) {
      toast.error('Enter a valid etid');
      return;
    }
    try {
      await addMutation.mutateAsync({
        section: effectiveSection,
        etid,
        menu_sec_menid:
          effectiveSection === 'sec' && secMenid !== '' ? Number(secMenid) : undefined,
      });
      toast.success(`Added etid #${etid}`);
      setAddOpen(false);
      setEtidInput('');
      setSecMenid('');
    } catch (e: any) {
      toast.error(e?.message ?? 'Add failed');
    }
  };

  const handleRemove = async (m: MenuSectionMember) => {
    if (!effectiveSection) return;
    if (!window.confirm(`Remove “${memberLabel(m)}” from ${effectiveSection}?`)) return;
    try {
      await removeMutation.mutateAsync({ section: effectiveSection, etid: m.etid });
      toast.success('Removed');
    } catch (e: any) {
      toast.error(e?.message ?? 'Remove failed');
    }
  };

  const swapOrder = async (index: number, dir: -1 | 1) => {
    if (!effectiveSection) return;
    const other = index + dir;
    if (other < 0 || other >= sortedMembers.length) return;

    if (!hasDedicatedOrder) {
      const ok = window.confirm(
        'This section shares entity_type.order_by with other non-main mid sections. Reordering here also changes those. Continue?',
      );
      if (!ok) return;
    }

    const next = [...sortedMembers];
    const tmp = next[index];
    next[index] = next[other];
    next[other] = tmp;

    const order = next.map((m, i) => ({ etid: m.etid, order: (i + 1) * 10 }));
    try {
      const res = await reorderMutation.mutateAsync({ section: effectiveSection, order });
      if (res?.warning) toast.warning(res.warning);
      else toast.success('Order updated');
    } catch (e: any) {
      toast.error(e?.message ?? 'Reorder failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center gap-2">
          <LayoutGrid className="h-4 w-4 text-gray-500" />
          <h2 className="text-sm font-medium text-gray-800">Page category strips</h2>
        </div>
        <p className="mb-1 text-xs text-gray-500">
          Scrollable category chips on the homepage and vertical pages (e.g. “Phones, Tablets &amp;
          Accessories”). Separate from the header blue bar — those are edited under Header
          categories.
        </p>
        <p className="mb-4 text-[11px] text-gray-400">
          Only the homepage strip has its own sort order; other strips share{' '}
          <code>order_by</code> with category sort elsewhere.
        </p>

        {sectionsLoading ? (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {Array.from({ length: 9 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {orderedSections.primary.map((s) => {
                const active = s.section === effectiveSection;
                return (
                  <button
                    key={s.section}
                    type="button"
                    onClick={() => setSelected(s.section)}
                    className={`rounded-md border px-3 py-2.5 text-left transition-colors ${
                      active
                        ? 'border-blue-200 bg-blue-50 text-blue-900'
                        : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <div className="text-sm font-medium">
                      {MENU_SECTION_LABELS[s.section] ?? s.section}
                    </div>
                    <div className="mt-0.5 text-[11px] text-gray-500">
                      {MENU_SECTION_SITE_HINTS[s.section] ?? `${s.count} categories`}
                    </div>
                    <div className="mt-0.5 text-[10px] text-gray-400">{s.count} in strip</div>
                  </button>
                );
              })}
            </div>

            {orderedSections.advanced.length > 0 && (
              <div>
                <button
                  type="button"
                  className="text-xs text-gray-500 underline-offset-2 hover:underline"
                  onClick={() => setShowAdvanced((v) => !v)}
                >
                  {showAdvanced ? 'Hide' : 'Show'} advanced flags (prim / sec)
                </button>
                {showAdvanced && (
                  <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                    {orderedSections.advanced.map((s) => {
                      const active = s.section === effectiveSection;
                      return (
                        <button
                          key={s.section}
                          type="button"
                          onClick={() => setSelected(s.section)}
                          className={`rounded-md border px-3 py-2.5 text-left transition-colors ${
                            active
                              ? 'border-blue-200 bg-blue-50 text-blue-900'
                              : 'border-dashed border-gray-200 bg-gray-50 text-gray-600 hover:bg-white'
                          }`}
                        >
                          <div className="text-sm font-medium">
                            {MENU_SECTION_LABELS[s.section] ?? s.section}
                          </div>
                          <div className="mt-0.5 text-[11px] text-gray-500">
                            {MENU_SECTION_SITE_HINTS[s.section]}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {effectiveSection && (
        <div className="overflow-hidden rounded-lg border bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-gray-50 px-4 py-2.5">
            <div>
              <span className="text-sm font-medium text-gray-800">
                {MENU_SECTION_LABELS[effectiveSection] ?? effectiveSection}
              </span>
              {MENU_SECTION_SITE_HINTS[effectiveSection] && (
                <span className="ml-2 text-[11px] font-normal text-gray-400">
                  {MENU_SECTION_SITE_HINTS[effectiveSection]}
                </span>
              )}
            </div>
            <Button size="sm" className="gap-1.5" onClick={() => setAddOpen(true)}>
              <Plus className="h-3.5 w-3.5" />
              Add entity type
            </Button>
          </div>

          {!hasDedicatedOrder && (
            <div className="flex items-start gap-2 border-b border-amber-100 bg-amber-50 px-4 py-2.5 text-xs text-amber-800">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
              <span>
                Reordering this section writes the shared <code>entity_type.order_by</code> and
                affects other non-main mid sections and general category sort.
              </span>
            </div>
          )}

          {membersLoading && (
            <div className="space-y-2 p-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          )}

          {!membersLoading && sortedMembers.length === 0 && (
            <div className="px-6 py-14 text-center text-sm text-gray-500">
              No entity types in this section.
            </div>
          )}

          {!membersLoading &&
            sortedMembers.map((m, index) => (
              <div
                key={m.etid}
                className="group flex items-center gap-3 border-b border-gray-50 px-4 py-2.5 hover:bg-gray-50/80"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{memberLabel(m)}</span>
                    <span className="font-mono text-[10px] text-gray-400">etid #{m.etid}</span>
                    {m.menu_sec_menid != null && (
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
                        sec menid #{m.menu_sec_menid}
                      </span>
                    )}
                    {(m.active === false || m.active === 0) && (
                      <span className="text-[10px] font-medium uppercase text-amber-600">
                        inactive
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-gray-400">
                    order {memberOrder(m, effectiveSection)}
                  </div>
                </div>

                <div className="flex items-center gap-0.5">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0"
                    disabled={index === 0 || reorderMutation.isPending}
                    onClick={() => swapOrder(index, -1)}
                    title="Move up"
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0"
                    disabled={
                      index >= sortedMembers.length - 1 || reorderMutation.isPending
                    }
                    onClick={() => swapOrder(index, 1)}
                    title="Move down"
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0 text-red-600"
                    onClick={() => handleRemove(m)}
                    title="Remove"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Add to {MENU_SECTION_LABELS[effectiveSection ?? ''] ?? effectiveSection}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="space-y-1.5">
              <Label htmlFor="mid-etid">Entity type id (etid)</Label>
              <Input
                id="mid-etid"
                type="number"
                value={etidInput}
                onChange={(e) => setEtidInput(e.target.value)}
                placeholder="12"
                autoFocus
              />
            </div>
            {effectiveSection === 'sec' && (
              <div className="space-y-1.5">
                <Label htmlFor="mid-sec-menid">Secondary subtree root (menu_sec_menid)</Label>
                <Input
                  id="mid-sec-menid"
                  type="number"
                  value={secMenid}
                  onChange={(e) => setSecMenid(e.target.value)}
                  placeholder="2"
                />
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAddOpen(false)}>
                Cancel
              </Button>
              <Button disabled={addMutation.isPending} onClick={handleAdd}>
                {addMutation.isPending ? 'Adding…' : 'Add'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
